import { MAILING_API } from '@Constants/backendConstants';
import type { ApiSendEmailRequest } from '@Model';
import { isOk, useSynqoHttp, type Result } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { Base64 } from 'js-base64';
import { useNotifyFailedRequest } from '@Hooks/useNotifyFailedRequest';

interface IPrepareEmail<T> {
    prepareEmail: (request: T) => Promise<void>;
    isPreparing: boolean;
    emailRequest: ApiSendEmailRequest | undefined;
    setEmailRequest: Dispatch<SetStateAction<ApiSendEmailRequest | undefined>>;
}

export function usePrepareEmail<T>(preparationUrl: string): IPrepareEmail<T> {
    const http = useSynqoHttp(`${MAILING_API}/emails`);
    const { notifyFailedRequest } = useNotifyFailedRequest({
        title: 'Email-Versand',
        message: 'Email-Versand konnte nicht vorbereitet werden.',
    });
    const [emailRequest, setEmailRequest] = useState<ApiSendEmailRequest>();

    const mutation = useMutation({
        mutationFn: (sendDeliverySlipRequest: T) =>
            http.post<Result<ApiSendEmailRequest>, T>(preparationUrl, sendDeliverySlipRequest),
    });

    async function prepareEmail(request: T) {
        const result = await mutation.mutateAsync(request);
        if (isOk(result)) {
            const emailRequest: ApiSendEmailRequest = { ...result.data, body: Base64.decode(result.data.body) };
            setEmailRequest(emailRequest);
        } else {
            notifyFailedRequest(result);
        }
    }

    return {
        prepareEmail,
        isPreparing: mutation.isPending,
        emailRequest,
        setEmailRequest,
    };
}
